// Application Insights Telemetry Service for distributed tracing
// Implements W3C Trace Context for correlation with backend services

import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';

// Tracing constants matching server-side TracingConstants.cs
export const TracingConstants = {
  TRACE_ID_HEADER: 'X-Trace-Id',
  SPAN_ID_HEADER: 'X-Span-Id',
  PARENT_SPAN_ID_HEADER: 'X-Parent-Span-Id',
  TRACE_ID_QUERY_PARAM: 'traceId',
  TRACE_ID_TELEMETRY_KEY: 'traceId',
  SPAN_ID_TELEMETRY_KEY: 'spanId',
  SESSION_ID_KEY: 'sessionId'
};

// Generate W3C-compatible trace ID (32 hex characters)
export const generateTraceId = () => {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
};

// Generate W3C-compatible span ID (16 hex characters)
export const generateSpanId = () => {
  const bytes = new Uint8Array(8);
  crypto.getRandomValues(bytes);
  return Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
};

// React plugin instance
const reactPlugin = new ReactPlugin();

// Application Insights instance - lazily initialized
let appInsights = null;
let isInitialized = false;

// Current trace context for the session
let currentTraceContext = {
  traceId: null,
  sessionId: null,
  rootSpanId: null
};

// Initialize Application Insights
export const initializeAppInsights = (connectionString) => {
  if (isInitialized) {
    return appInsights;
  }

  if (!connectionString) {
    console.warn('[AppInsights] No connection string provided, telemetry will be disabled');
    return null;
  }

  try {
    appInsights = new ApplicationInsights({
      config: {
        connectionString: connectionString,
        extensions: [reactPlugin],
        enableAutoRouteTracking: true,
        enableRequestHeaderTracking: true,
        enableResponseHeaderTracking: true,
        enableAjaxPerfTracking: true,
        maxAjaxCallsPerView: 100,
        disableFetchTracking: false,
        disableAjaxTracking: false,
        enableCorsCorrelation: true,
        correlationHeaderExcludedDomains: [],
        enableW3cHeaders: true
      }
    });

    appInsights.loadAppInsights();
    isInitialized = true;
    console.log('[AppInsights] Initialized successfully');
    return appInsights;
  } catch (error) {
    console.error('[AppInsights] Failed to initialize:', error);
    return null;
  }
};

// Start a new trace for a session
export const startSessionTrace = (sessionId) => {
  currentTraceContext = {
    traceId: generateTraceId(),
    sessionId: sessionId,
    rootSpanId: generateSpanId()
  };

  trackEvent('SessionTraceStarted', {
    sessionId: sessionId,
    traceId: currentTraceContext.traceId
  });

  console.log(`[Tracing] Started trace for session ${sessionId}: ${currentTraceContext.traceId}`);
  return currentTraceContext;
};

// Get current trace context
export const getTraceContext = () => {
  return { ...currentTraceContext };
};

// Get trace ID for current session
export const getCurrentTraceId = () => {
  if (!currentTraceContext.traceId) {
    // Generate a new trace ID if none exists
    currentTraceContext.traceId = generateTraceId();
    console.log(`[Tracing] Generated new trace ID: ${currentTraceContext.traceId}`);
  }
  return currentTraceContext.traceId;
};

// Track custom event with trace context
export const trackEvent = (name, properties = {}) => {
  const enrichedProperties = {
    ...properties,
    [TracingConstants.TRACE_ID_TELEMETRY_KEY]: currentTraceContext.traceId || 'no-trace',
    [TracingConstants.SESSION_ID_KEY]: currentTraceContext.sessionId || 'no-session',
    timestamp: new Date().toISOString()
  };

  if (appInsights) {
    appInsights.trackEvent({ name }, enrichedProperties);
  }

  // Always log to console for debugging
  console.log(`[Telemetry] ${name}:`, enrichedProperties);
};

// Track exception with trace context
export const trackException = (error, properties = {}) => {
  const enrichedProperties = {
    ...properties,
    [TracingConstants.TRACE_ID_TELEMETRY_KEY]: currentTraceContext.traceId || 'no-trace',
    [TracingConstants.SESSION_ID_KEY]: currentTraceContext.sessionId || 'no-session',
    errorMessage: error?.message || String(error),
    errorStack: error?.stack,
    timestamp: new Date().toISOString()
  };

  if (appInsights) {
    appInsights.trackException({ exception: error }, enrichedProperties);
  }

  console.error(`[Telemetry Exception] ${error?.message}:`, enrichedProperties);
};

// Track dependency call (API, SignalR, etc.)
export const trackDependency = (name, target, duration, success, properties = {}) => {
  const enrichedProperties = {
    ...properties,
    [TracingConstants.TRACE_ID_TELEMETRY_KEY]: currentTraceContext.traceId || 'no-trace',
    [TracingConstants.SESSION_ID_KEY]: currentTraceContext.sessionId || 'no-session'
  };

  if (appInsights) {
    appInsights.trackDependencyData({
      id: generateSpanId(),
      name: name,
      target: target,
      duration: duration,
      success: success,
      properties: enrichedProperties
    });
  }

  console.log(`[Telemetry Dependency] ${name} -> ${target} (${duration}ms, success: ${success}):`, enrichedProperties);
};

// Track page view
export const trackPageView = (name, properties = {}) => {
  const enrichedProperties = {
    ...properties,
    [TracingConstants.TRACE_ID_TELEMETRY_KEY]: currentTraceContext.traceId || 'no-trace'
  };

  if (appInsights) {
    appInsights.trackPageView({ name }, enrichedProperties);
  }
};

// Clear trace context (e.g., on session end)
export const clearTraceContext = () => {
  const previousTraceId = currentTraceContext.traceId;
  currentTraceContext = {
    traceId: null,
    sessionId: null,
    rootSpanId: null
  };

  console.log(`[Tracing] Cleared trace context (was: ${previousTraceId})`);
};

// Export react plugin for component usage
export const getReactPlugin = () => reactPlugin;

// Export Application Insights instance
export const getAppInsights = () => appInsights;

const appInsightsService = {
  initializeAppInsights,
  startSessionTrace,
  getTraceContext,
  getCurrentTraceId,
  trackEvent,
  trackException,
  trackDependency,
  trackPageView,
  clearTraceContext,
  getReactPlugin,
  getAppInsights,
  TracingConstants,
  generateTraceId,
  generateSpanId
};

export default appInsightsService;
